import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import Component from '../../components/Component';
import {
  createDummyCheckout,
  getPaymentTransaction,
  getPaymentsSummary,
  isPaymentsApiSuccess,
  listPaymentTransactions,
  verifyDummyPayment,
} from '../../services/paymentsApi';
import {
  DUMMY_STATUS_HELP,
  PAGE_LIMIT,
  PAYMENT_TABS,
  bannerCopy,
  checkoutBody,
  emptyMessage,
  fanPrimary,
  formatRupees,
  hasNextPage,
  payButtonLabel,
  tabLabel,
} from './paymentsModel';
import { loadRazorpay, openRazorpay } from './razorpayCheckout';

function formatWhen(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

function statusColor(status) {
  if (status === 'paid') return 'success';
  if (status === 'created') return 'warning';
  return 'default';
}

function modeColor(mode) {
  if (mode === 'live') return 'error';
  if (mode === 'test') return 'info';
  return 'default';
}

function bannerSeverity(keyMode) {
  if (keyMode === 'live' || keyMode === 'unconfigured') return 'warning';
  return 'info';
}

function PaymentTable({ items, selectedId, onSelect }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Amount</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Mode</TableCell>
            <TableCell>Fan</TableCell>
            <TableCell>Razorpay</TableCell>
            <TableCell>When</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const user = item.user;
            return (
              <TableRow
                key={item.id}
                hover
                selected={selectedId === item.id}
                onClick={() => onSelect(item.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {formatRupees(item.amount_inr)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.currency || 'INR'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={item.status || '—'} color={statusColor(item.status)} />
                  {item.direction ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.direction}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={item.mode || '—'}
                    color={modeColor(item.mode)}
                    variant={item.mode === 'live' ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{fanPrimary(user)}</Typography>
                  {user?.email ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {user.email}
                    </Typography>
                  ) : null}
                  {user?.phone ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {user.phone}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }} display="block">
                    {item.razorpay_order_id || '—'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                    display="block"
                  >
                    {item.razorpay_payment_id || '—'}
                  </Typography>
                  {item.receipt ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.receipt}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" display="block">
                    Created {formatWhen(item.created_at)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {item.paid_at ? `Paid ${formatWhen(item.paid_at)}` : 'Not paid'}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DetailField({ label, children }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
        {children || '—'}
      </Typography>
    </Box>
  );
}

function PaymentDetail({ item, onClose }) {
  const user = item.user;
  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <Typography variant="h6">Payment {item.id}</Typography>
        <Button size="small" variant="text" onClick={onClose}>
          Close
        </Button>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 1.5,
        }}
      >
        <DetailField label="Amount">{formatRupees(item.amount_inr)}</DetailField>
        <DetailField label="Amount (paise)">{item.amount_paise == null ? '—' : String(item.amount_paise)}</DetailField>
        <DetailField label="Currency">{item.currency}</DetailField>
        <DetailField label="Status">{item.status}</DetailField>
        <DetailField label="Mode">{item.mode}</DetailField>
        <DetailField label="Direction">{item.direction}</DetailField>
        <DetailField label="Receipt">{item.receipt}</DetailField>
        <DetailField label="Razorpay order">{item.razorpay_order_id}</DetailField>
        <DetailField label="Razorpay payment">{item.razorpay_payment_id}</DetailField>
        <DetailField label="Fan">{fanPrimary(user)}</DetailField>
        <DetailField label="Email">{user?.email}</DetailField>
        <DetailField label="Phone">{user?.phone}</DetailField>
        <DetailField label="Username">{user?.username}</DetailField>
        <DetailField label="User id">{user?.id == null ? '—' : String(user.id)}</DetailField>
        <DetailField label="Created">{formatWhen(item.created_at)}</DetailField>
        <DetailField label="Paid">{item.paid_at ? formatWhen(item.paid_at) : 'Not paid'}</DetailField>
      </Box>
    </Box>
  );
}

export function PaymentsPanel() {
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState('');
  const [summaryTick, setSummaryTick] = useState(0);
  const [tab, setTab] = useState('dummy');
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [list, setList] = useState(null);
  const [listError, setListError] = useState('');
  const [loading, setLoading] = useState(true);
  const [listTick, setListTick] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTick, setDetailTick] = useState(0);
  const [amountRupees, setAmountRupees] = useState('1');
  const [paying, setPaying] = useState(false);
  const [payNotice, setPayNotice] = useState('');
  const [payError, setPayError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      const res = await getPaymentsSummary();
      if (!active) return;
      if (!isPaymentsApiSuccess(res)) {
        setSummary(null);
        setSummaryError(res?.message || 'Failed to load payments summary');
        return;
      }
      setSummaryError('');
      setSummary(res.data || null);
    })();
    return () => {
      active = false;
    };
  }, [summaryTick]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setList(null);
    setListError('');
    (async () => {
      const res = await listPaymentTransactions({
        view: tab,
        limit: PAGE_LIMIT,
        offset,
        status,
        q,
      });
      if (!active) return;
      if (!isPaymentsApiSuccess(res)) {
        setList(null);
        setListError(res?.message || 'Failed to load payments');
        setLoading(false);
        return;
      }
      setList(res.data || null);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [tab, offset, status, q, listTick]);

  useEffect(() => {
    if (selectedId == null) return undefined;
    let active = true;
    (async () => {
      setDetailLoading(true);
      setDetailError('');
      const res = await getPaymentTransaction(selectedId);
      if (!active) return;
      if (!isPaymentsApiSuccess(res)) {
        setDetail(null);
        setDetailError(res?.message || 'Failed to load payment');
        setDetailLoading(false);
        return;
      }
      setDetail(res.data || null);
      setDetailLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [selectedId, detailTick]);

  const selectTab = (view) => {
    setTab(view);
    setOffset(0);
    setSelectedId(null);
    setDetail(null);
    setDetailError('');
    if (view !== 'dummy' && view !== 'all') setStatus('');
  };

  const refresh = () => {
    setSummaryTick((n) => n + 1);
    setListTick((n) => n + 1);
    if (selectedId != null) setDetailTick((n) => n + 1);
  };

  const reloadAfterCheckout = () => {
    setSelectedId(null);
    setDetail(null);
    setDetailError('');
    setOffset(0);
    setSummaryTick((n) => n + 1);
    setListTick((n) => n + 1);
  };

  const runPay = async (event) => {
    event.preventDefault();
    const body = checkoutBody(amountRupees);
    if (body == null) {
      setPayNotice('');
      setPayError('Enter an amount in rupees.');
      return;
    }

    setPaying(true);
    setPayError('');
    setPayNotice('');
    let orderCreated = false;
    try {
      const created = await createDummyCheckout(body);
      if (!isPaymentsApiSuccess(created)) {
        throw new Error(created?.message || 'Checkout failed');
      }
      orderCreated = true;
      await loadRazorpay();
      const verified = await openRazorpay(created.data, verifyDummyPayment);
      setPayNotice(
        verified?.payment_id ? `Payment verified. ${verified.payment_id}` : 'Payment verified.'
      );
      reloadAfterCheckout();
    } catch (error) {
      if (orderCreated) reloadAfterCheckout();
      if (error?.message === 'cancelled') {
        setPayNotice('Checkout closed. Verify was not called. The order stays created.');
      } else {
        setPayError(error instanceof Error ? error.message : 'Payment failed');
      }
    } finally {
      setPaying(false);
    }
  };

  const items = Array.isArray(list?.items) ? list.items : [];
  const total = Number(list?.total || 0);
  const pageOffset = Number(list?.offset ?? offset);
  const showNext = list ? hasNextPage(pageOffset, items.length, total) : false;
  const banner = bannerCopy(summary?.key_mode);
  const blankReason = list?.available === false ? list.reason : '';
  const blankCopy = list && !loading && items.length === 0 ? emptyMessage(tab, list) : '';
  const statusFilter = tab === 'dummy' || tab === 'all';

  return (
    <div className="page-body">
      <div className="container-fluid">
        <div className="page-header">
          <div className="row">
            <div className="col-sm-8">
              <h3>Payments</h3>
              <Typography variant="body2" color="text.secondary">
                Razorpay orders recorded by Dugout. Dummy checkout opens Razorpay’s payment window. The key secret stays on the server.
              </Typography>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
              <Button variant="contained" onClick={refresh} disabled={loading}>
                Refresh
              </Button>
              {loading ? <CircularProgress size={22} /> : null}
            </Box>

            {summaryError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {summaryError}
              </Alert>
            ) : null}

            {banner ? (
              <Alert severity={bannerSeverity(summary?.key_mode)} sx={{ mb: 2 }}>
                {banner}
              </Alert>
            ) : null}

            <Tabs
              value={tab}
              onChange={(_, value) => selectTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2 }}
            >
              {PAYMENT_TABS.map((item) => (
                <Tab key={item.view} value={item.view} label={tabLabel(item.view, summary)} />
              ))}
            </Tabs>

            {tab === 'dummy' ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  created — {DUMMY_STATUS_HELP.created}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  paid — {DUMMY_STATUS_HELP.paid}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A row that stays created means verify did not run.
                </Typography>
              </Box>
            ) : null}

            {tab === 'all' ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Every order we created, test and live, newest first. Includes payments that are still created.
              </Typography>
            ) : null}

            {tab === 'inbound' ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Money that actually came in. Only paid orders. Created rows stay on Dummy and All transactions until verify succeeds.
              </Typography>
            ) : null}

            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                setOffset(0);
                setQ(qInput.trim());
              }}
              sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}
            >
              {statusFilter ? (
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={status}
                  onChange={(event) => {
                    setStatus(event.target.value);
                    setOffset(0);
                  }}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="created">created</MenuItem>
                  <MenuItem value="paid">paid</MenuItem>
                </TextField>
              ) : null}
              <TextField
                size="small"
                label="Search"
                value={qInput}
                onChange={(event) => setQInput(event.target.value)}
                placeholder="Receipt, order, payment, user, email, phone"
                sx={{ minWidth: 280 }}
              />
              <Button type="submit" variant="outlined" size="small">
                Search
              </Button>
            </Box>

            {tab === 'dummy' && summary?.key_mode === 'test' ? (
              <Box
                component="form"
                onSubmit={runPay}
                sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}
              >
                <TextField
                  size="small"
                  label="Amount (rupees)"
                  value={amountRupees}
                  onChange={(event) => setAmountRupees(event.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={{ width: 160 }}
                />
                <Button type="submit" variant="contained" disabled={paying}>
                  {payButtonLabel(amountRupees)}
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ flexBasis: '100%' }}>
                  Card 4111 1111 1111 1111, any CVV, any future expiry, then Success. OTP 1234 if asked. UPI success@razorpay also works. Closing the window leaves the order created.
                </Typography>
              </Box>
            ) : null}

            {payError ? (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPayError('')}>
                {payError}
              </Alert>
            ) : null}
            {payNotice ? (
              <Alert severity={payNotice.startsWith('Payment verified') ? 'success' : 'info'} sx={{ mb: 2 }} onClose={() => setPayNotice('')}>
                {payNotice}
              </Alert>
            ) : null}

            {listError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {listError}
              </Alert>
            ) : null}

            {blankReason ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {blankReason}
              </Alert>
            ) : null}

            {loading && !list ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <PaymentTable items={items} selectedId={selectedId} onSelect={setSelectedId} />
                {blankCopy ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {blankCopy}
                  </Typography>
                ) : null}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={pageOffset <= 0 || loading}
                    onClick={() => setOffset(Math.max(0, pageOffset - PAGE_LIMIT))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!showNext || loading}
                    onClick={() => setOffset(pageOffset + PAGE_LIMIT)}
                  >
                    Next
                  </Button>
                  {total > 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {pageOffset + 1}–{pageOffset + items.length} of {total}
                    </Typography>
                  ) : null}
                </Box>
              </>
            )}

            {detailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : null}
            {detailError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {detailError}
              </Alert>
            ) : null}
            {detail ? <PaymentDetail item={detail} onClose={() => { setSelectedId(null); setDetail(null); }} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Payments() {
  return (
    <Component>
      <PaymentsPanel />
    </Component>
  );
}

export default Payments;
